import { Texture } from 'pixi.js';
import type { Sprite, Rectangle } from 'pixi.js';
import { clamp } from '@softgames/math-utils';

interface CropTexture extends Texture {
  originalFrame: Rectangle;
  originalOrigin: Rectangle;
}

type Direction = 'x' | 'y' | 'width' | 'height';

function createCropTexture(texture: Texture): CropTexture {
  const cropTexture = new Texture({
    source: texture.source,
    frame: texture.frame.clone(),
    orig: texture.orig.clone(),
    rotate: texture.rotate,
  });
  (cropTexture as CropTexture).originalFrame = cropTexture.frame.clone();
  (cropTexture as CropTexture).originalOrigin = cropTexture.orig.clone();
  return cropTexture as CropTexture;
}

function isCropTexture(texture: Texture): texture is CropTexture {
  return 'originalFrame' in texture;
}

export function cropSprite(sprite: Sprite, progress: number, direction: Direction): Sprite {
  if (sprite.destroyed) return sprite;

  const progressClamped = clamp(progress, 0, 1);

  if (!isCropTexture(sprite.texture)) {
    sprite.texture = createCropTexture(sprite.texture);
  }

  const orgFrame = (sprite.texture as CropTexture).originalFrame;

  const isTextureRotated = sprite.texture.rotate === 2;
  const xOrWidth = direction === 'x' || direction === 'width';

  if (isTextureRotated) {
    const sizeProp = xOrWidth ? 'height' : 'width';
    const newSize = orgFrame[sizeProp] * progressClamped;
    sprite.texture.frame[sizeProp] = newSize;

    sprite.texture.orig.width = sprite.texture.frame.height;
    sprite.texture.orig.height = sprite.texture.frame.width;

    if (direction === 'x') {
      sprite.texture.frame.y = orgFrame.y + (orgFrame.height - newSize);
    } else if (direction === 'height') {
      sprite.texture.frame.x = orgFrame.x + (orgFrame.width - newSize);
    }
  } else {
    const sizeProp = xOrWidth ? 'width' : 'height';
    const newSize = orgFrame[sizeProp] * progressClamped;
    sprite.texture.frame[sizeProp] = newSize;
    sprite.texture.orig[sizeProp] = newSize;

    if (direction === 'x') {
      sprite.texture.frame.x = orgFrame.x + (orgFrame.width - newSize);
    } else if (direction === 'y') {
      sprite.texture.frame.y = orgFrame.y + (orgFrame.height - newSize);
      sprite.texture.frame.height = newSize;
    }
  }

  sprite.texture.updateUvs();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  // Sprite doesn't pick up changes on the textures
  sprite.onViewUpdate();

  return sprite;
}
